import { Fragment, ReactNode, useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Drawer, IconButton, Link, Menu, MenuItem, Paper, Snackbar, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router";
import * as Sentry from "@sentry/react";
import { useApi } from "../../hooks/useApi";
import { useStateValue } from "../../context/StateValueContext";
import { hasPermission } from "../../functions/permissionUtils";
import { PERMISSIONS } from "../../constants/permissions";
import { Agent } from "../../types/user";
import { formatCurrency } from "../../utils/formatCurrency";
import { LANGUAGE_NAMES, paypalStatusLabel, reasonLabel } from "./labels";
import { InquiryDetail as InquiryDetailData, TemplateLanguage } from "./types";
import { AcceptBody, OfferBody, useInquiryDetail } from "./useInquiryDetail";
import { formatRelative } from "./time";
import { INK, OUTLINED, PROOF_COLORS } from "./styles";
import InquiryThread from "./InquiryThread";
import InquiryComposer from "./InquiryComposer";
import InquiryFactsPanel from "./InquiryFactsPanel";
import MakeOfferDialog from "./MakeOfferDialog";
import AcceptClaimDialog from "./AcceptClaimDialog";
import FindOrderDialog from "../../components/evidence/FindOrderDialog";

const META_LINE = { fontSize: 12, color: INK.muted, display: 'flex', flexWrap: 'wrap', alignItems: 'center' };

const PROCESSING_WINDOW_MINUTES = 15;
const OFFER_TEMPLATE_KEYS = new Set(['offer_partial', 'offer_partial_and_reship', 'offer_improved', 'return_or_keep']);

function composerDisabledReason(detail: InquiryDetailData): string {
  const sellerMessages = detail.messages.filter((m) => m.postedBy === 'SELLER');
  const lastEvent = sellerMessages[sellerMessages.length - 1]?.at ?? detail.dispute.openedAt;
  const processing = detail.dispute.status === 'UNDER_REVIEW' && lastEvent !== null
    && dayjs().diff(dayjs(lastEvent), 'minute') < PROCESSING_WINDOW_MINUTES;
  return processing
    ? 'PayPal is still processing this inquiry. Refresh in a minute.'
    : 'PayPal no longer accepts messages on this inquiry';
}

function statusChipColors(status: string | null, outcomeCode: string | null) {
  if (status === 'UNDER_REVIEW') return { color: '#b54708', borderColor: '#fedf89', bgcolor: '#fffaeb' };
  if (status === 'RESOLVED' && outcomeCode?.includes('SELLER_FAVOUR')) return { color: '#067647', borderColor: '#abefc6', bgcolor: '#ecfdf3' };
  if (status?.startsWith('WAITING_FOR')) return { color: INK.muted, borderColor: INK.control, bgcolor: '#fff' };
  return { color: INK.body, borderColor: INK.border, bgcolor: INK.divider };
}

function MetaLine({ items, sx, children }: { items: ReactNode[]; sx?: object; children?: ReactNode }) {
  return (
    <Box sx={{ ...META_LINE, ...sx }}>
      {items.filter(Boolean).map((item, i) => (
        <Fragment key={i}>
          {i > 0 && <Box component="span" sx={{ mx: 0.75 }}>·</Box>}
          {item}
        </Fragment>
      ))}
      {children}
    </Box>
  );
}

type Props = {
  id: number | null;
  onGone: () => void;
  onNext: () => void;
  hasNext: boolean;
  onChanged: () => void;
};

export default function InquiryDetail({ id, onGone, onNext, hasNext, onChanged }: Props) {
  const inquiry = useInquiryDetail(id);
  const { detail, gone, setSnackbar } = inquiry;
  const { get } = useApi();
  const [{ user, agents }, dispatch] = useStateValue();
  const canAssign = hasPermission(user, PERMISSIONS.DISPUTES_ASSIGN);
  const canOpenEvidence = hasPermission(user, PERMISSIONS.DISPUTE_EVIDENCE);
  const navigate = useNavigate();
  const { search } = useLocation();
  const [dialog, setDialog] = useState<'offer' | 'accept' | 'order' | null>(null);
  const [agentsError, setAgentsError] = useState(false);
  const [me, setMe] = useState<{ userId: number; firstName: string } | null>(null);
  const [meError, setMeError] = useState(false);
  const [meKey, setMeKey] = useState(0);
  const wide = useMediaQuery(useTheme().breakpoints.up('xl'));
  const [orderOpen, setOrderOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => { if (gone) onGone(); }, [gone, onGone]);

  useEffect(() => { setDialog(null); setMoreAnchor(null); }, [id, gone]);

  useEffect(() => { setOrderOpen(false); }, [id]);

  useEffect(() => { if (wide) setOrderOpen(false); }, [wide]);

  useEffect(() => {
    get<{ userId: number; firstName: string }>('/api/users/me')
      .then((res) => { setMeError(false); setMe(res); })
      .catch((err) => { Sentry.captureException(err); setMeError(true); });
  }, [get, meKey]);

  useEffect(() => {
    if (!canAssign || agents.length > 0) return;
    get<Agent[]>('/api/users/agents')
      .then((res) => { setAgentsError(false); dispatch({ type: 'SET_AGENTS', agents: res }); })
      .catch((err) => { Sentry.captureException(err); setAgentsError(true); });
  }, [canAssign, agents.length, get, dispatch]);

  const assign = async (userId: number | null) => {
    if (await inquiry.assign(userId)) onChanged();
  };

  const sendReply = async () => {
    if (await inquiry.send()) onChanged();
  };

  const sendAndNext = async (offerAmount: number | null = null) => {
    const delivered = inquiry.send(offerAmount);
    if (!delivered) return;
    if (hasNext) onNext();
    if (await delivered) onChanged();
  };

  const syncInquiry = async () => {
    if (await inquiry.sync()) onChanged();
  };

  const retryReply = async () => {
    if (await inquiry.retry()) onChanged();
  };

  const openDialog = (which: 'offer' | 'accept') => {
    inquiry.setActionError(null);
    setDialog(which);
  };

  const offer = detail && inquiry.templateKey !== null && OFFER_TEMPLATE_KEYS.has(inquiry.templateKey)
    ? { pct: inquiry.pct, amount: Math.round(detail.dispute.amount * inquiry.pct / 100), total: detail.dispute.amount, currency: detail.dispute.currency, allowed: detail.allowed.offer && detail.offerTypes.includes('REFUND') }
    : null;

  const sendOffer = async () => {
    if (offer && await inquiry.send(offer.amount)) onChanged();
  };

  const switchLanguage = (language: TemplateLanguage) => {
    inquiry.setLanguage(language);
    if (inquiry.templateUntouched && language !== inquiry.language) setSnackbar((s) => (s?.undo ? s : { message: `Template switched to ${LANGUAGE_NAMES[language]}`, severity: 'success' }));
  };

  const submitOffer = async (body: OfferBody) => {
    const ok = await inquiry.submitAction('offer', body);
    if (ok) onChanged();
    return ok;
  };

  const submitAccept = async (body: AcceptBody) => {
    const ok = await inquiry.submitAction('accept', body);
    if (ok) onChanged();
    return ok;
  };

  const viewId = inquiry.snackbar?.viewId;

  const assigneeId = detail?.dispute.assignedUserId ?? null;
  const others: { userId: number | null; name: string; email?: string }[] = assigneeId !== null && !agents.some((a) => a.userId === assigneeId)
    ? [...agents, { userId: assigneeId, name: detail?.dispute.assignedFirstName ?? 'Unknown user' }]
    : agents;
  const assigneeOptions: { userId: number | null; name: string; email?: string }[] = [
    ...(me ? [{ userId: me.userId, name: 'Me' }] : []),
    { userId: null, name: 'Unassigned' },
    ...others.filter((a) => a.userId !== me?.userId).sort((a, b) => a.name.localeCompare(b.name)),
  ];

  const factsPanel = detail && (
    <InquiryFactsPanel
      payment={detail.payment}
      facts={detail.facts}
      factsAt={detail.factsAt}
      currency={detail.dispute.currency}
      timeline={detail.timeline}
      onRefresh={inquiry.refreshFacts}
      refreshing={inquiry.refreshing}
      loading={detail.factsRefreshing && inquiry.polling && !detail.facts.orderName && !detail.facts.error}
      onFindOrder={() => setDialog('order')}
    />
  );

  const proof = detail && detail.queue !== 'closed' ? detail.proof : null;
  const proofOwed = !!detail?.proofRequested;

  const updating = inquiry.syncing || (!!detail?.refreshing && inquiry.polling);
  const synced = detail?.syncedAt ? `Synced ${formatRelative(detail.syncedAt)}` : "Not synced yet";

  return (
    <Paper
      elevation={0}
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        minHeight: { xs: '70vh', lg: 0 },
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${INK.border}`,
        borderRadius: '10px',
        overflow: 'hidden',
        fontSize: 13,
        color: INK.text,
      }}
    >
      {id === null ? (
        <Typography sx={{ fontSize: 13, color: INK.muted, m: 'auto' }}>Select an inquiry</Typography>
      ) : gone ? (
        <Alert severity="info" sx={{ m: 2 }}>This inquiry is no longer open</Alert>
      ) : inquiry.error ? (
        <Alert severity="error" sx={{ m: 2 }}>{inquiry.error}</Alert>
      ) : !detail ? (
        <CircularProgress size={24} sx={{ m: 'auto' }} />
      ) : (
        <>
          <Box
            sx={{
              p: '14px 22px',
              borderBottom: `1px solid ${INK.border}`,
              bgcolor: '#fff',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: INK.text }}>
                  {detail.dispute.buyerName} · {formatCurrency(detail.dispute.amount, detail.dispute.currency)} · {reasonLabel(detail.dispute.reason)}
                </Typography>
                {proofOwed || proof?.evidenceSentAt ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={proofOwed ? 'Proof requested' : 'Evidence sent'}
                    sx={{ height: 20, fontSize: 11, ...PROOF_COLORS[proofOwed ? 'requested' : 'sent'] }}
                  />
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={paypalStatusLabel(detail.dispute.status, detail.dispute.outcomeCode)}
                    sx={{ height: 20, fontSize: 11, ...statusChipColors(detail.dispute.status, detail.dispute.outcomeCode) }}
                  />
                )}
              </Box>
              <MetaLine sx={{ mt: '2px' }} items={[
                <span>{detail.dispute.paypalDisputeKey}</span>,
                <span>{detail.dispute.companyName}</span>,
                detail.dispute.brandName && detail.dispute.brandName !== detail.dispute.companyName && <span>{detail.dispute.brandName}</span>,
                <Link href={detail.dispute.paypalUrl} target="_blank" rel="noopener noreferrer">Open in PayPal</Link>,
              ]} />
              <MetaLine items={[
                detail.dispute.openedAt && <span>Opened {dayjs(detail.dispute.openedAt).format('MMM D, HH:mm')}</span>,
                detail.dispute.sellerDueAt && <span>Reply due {dayjs(detail.dispute.sellerDueAt).format('MMM D, HH:mm')}</span>,
                <span>{synced}</span>,
              ]}>
                <IconButton size="small" aria-label="Refresh" title={updating ? "Updating from PayPal…" : "Re-read the inquiry from PayPal"} onClick={syncInquiry} disabled={updating} sx={{ ml: 0.5, p: '2px' }}>
                  {updating ? <CircularProgress size={14} /> : <RefreshOutlined sx={{ fontSize: 16 }} />}
                </IconButton>
              </MetaLine>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              {meError && (
                <Typography sx={{ fontSize: 13, color: INK.muted }}>
                  Couldn't load your user{' '}
                  <Link component="button" onClick={() => setMeKey((k) => k + 1)} sx={{ fontSize: 13, verticalAlign: 'baseline' }}>Retry</Link>
                </Typography>
              )}
              {me && assigneeId !== me.userId && (
                <Button variant="outlined" size="small" sx={OUTLINED} disabled={inquiry.assigning} onClick={() => assign(me.userId)}>Assign to me</Button>
              )}
              {me && assigneeId === me.userId && !canAssign && (
                <Button variant="outlined" size="small" sx={OUTLINED} disabled={inquiry.assigning} onClick={() => assign(null)}>Hand back</Button>
              )}
              {canAssign && (agentsError
                ? <Typography sx={{ fontSize: 13, color: INK.muted }}>Couldn't load agents</Typography>
                : (
                  <Autocomplete
                    size="small"
                    disableClearable
                    disabled={inquiry.assigning}
                    options={assigneeOptions}
                    value={assigneeOptions.find((a) => a.userId === assigneeId) ?? assigneeOptions[0]}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.userId === value.userId}
                    filterOptions={(options, { inputValue }) => options.filter((o) => [...o.name.toLowerCase().split(' '), o.email?.toLowerCase() ?? ''].some((word) => word.startsWith(inputValue.trim().toLowerCase())))}
                    renderOption={({ key, ...props }, option) => (
                      <Box component="li" key={key} {...props} sx={{ flexDirection: 'column', alignItems: 'flex-start !important' }}>
                        <span>{option.name}</span>
                        {option.email && <Box component="span" sx={{ fontSize: 11, color: INK.muted }}>{option.email}</Box>}
                      </Box>
                    )}
                    onChange={(_, option) => assign(option.userId)}
                    renderInput={(params) => <TextField {...params} slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': 'Assignee' } }} />}
                    slotProps={{ popper: { sx: { minWidth: 260 } } }}
                    sx={{ minWidth: 160, '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { py: 0, pl: '10px', fontSize: 13, borderRadius: '8px' }, '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': { py: '6px' } }}
                  />
                ))}
              {!wide && <Button variant="outlined" size="small" sx={OUTLINED} onClick={() => setOrderOpen(true)}>Order</Button>}
              <Button variant="outlined" size="small" sx={OUTLINED} disabled={!detail.allowed.offer} onClick={() => openDialog('offer')}>Make offer</Button>
              <Button variant="outlined" size="small" sx={OUTLINED} aria-label="More actions" aria-haspopup="menu" onClick={(e) => setMoreAnchor(e.currentTarget)}>More ▾</Button>
              <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
                <MenuItem component="a" href={detail.dispute.paypalUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMoreAnchor(null)} sx={{ fontSize: 13 }}>Open in PayPal</MenuItem>
                <MenuItem disabled={!detail.allowed.accept} onClick={() => { setMoreAnchor(null); openDialog('accept'); }} sx={{ fontSize: 13, color: INK.danger }}>Accept claim and refund…</MenuItem>
              </Menu>
              <Button
                variant="outlined"
                size="small"
                sx={OUTLINED}
                onClick={onNext}
                disabled={!hasNext}
                title={hasNext ? "Next inquiry without replying" : "No more inquiries on this page"}
              >
                Next ›
              </Button>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', lg: 'row' } }}>
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: INK.thread }}>
              <InquiryThread inquiryId={id} evidenceHref={canOpenEvidence ? `/disputes/${detail.dispute.disputeId}/evidence` : null} messages={inquiry.messages} proof={proof} proofOwed={proofOwed} onRetry={retryReply} onUndo={inquiry.undoSend} />
              <Box sx={{ flex: '0 0 auto', p: '0 22px 18px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {inquiry.templatesError && <Alert severity="error">{inquiry.templatesError}</Alert>}
                {dialog === null && inquiry.actionError && <Alert severity="error">{inquiry.actionError}</Alert>}
                <InquiryComposer
                  templates={inquiry.templates}
                  templateKey={inquiry.templateKey}
                  onTemplate={inquiry.applyTemplate}
                  pct={inquiry.pct}
                  onPct={inquiry.setPct}
                  language={inquiry.language}
                  languageDetected={inquiry.languageDetected}
                  onLanguage={switchLanguage}
                  draft={inquiry.draft}
                  onDraft={inquiry.setDraft}
                  attachment={inquiry.attachment}
                  onAttachment={inquiry.setAttachment}
                  onSend={sendReply}
                  onSendAndNext={() => sendAndNext()}
                  offer={offer}
                  onSendOffer={sendOffer}
                  onSendOfferAndNext={() => { if (offer) sendAndNext(offer.amount); }}
                  sending={inquiry.messages[inquiry.messages.length - 1]?.pending === true}
                  disabled={!detail.allowed.message}
                  disabledReason={composerDisabledReason(detail)}
                  placeholder={proofOwed ? "Write a reply… (a message won't answer the proof request)" : undefined}
                  maxLength={2000}
                />
              </Box>
            </Box>
            {wide && factsPanel}
          </Box>
          {!wide && (
            <Drawer
              anchor="right"
              variant="temporary"
              open={orderOpen}
              onClose={() => setOrderOpen(false)}
              slotProps={{ paper: { 'aria-label': 'Order panel', sx: { width: 'min(360px, 100vw)' } } }}
            >
              {factsPanel}
            </Drawer>
          )}
          {dialog === 'offer' && (
            <MakeOfferDialog
              open
              onClose={() => setDialog(null)}
              amount={detail.dispute.amount}
              currency={detail.dispute.currency}
              offerTypes={detail.offerTypes}
              onSubmit={submitOffer}
              submitting={inquiry.acting}
              error={inquiry.actionError}
            />
          )}
          {dialog === 'order' && (
            <FindOrderDialog
              disputeId={String(detail.dispute.disputeId)}
              searchPath={`/api/paypal-inquiries/${detail.dispute.disputeId}/order-search`}
              description="The reply templates and the order panel use the order you pick."
              onClose={() => setDialog(null)}
              onPick={inquiry.attachOrder}
            />
          )}
          {dialog === 'accept' && (
            <AcceptClaimDialog
              open
              onClose={() => setDialog(null)}
              amount={detail.dispute.amount}
              currency={detail.dispute.currency}
              acceptTypes={detail.acceptTypes}
              onSubmit={submitAccept}
              submitting={inquiry.acting}
              error={inquiry.actionError}
            />
          )}
        </>
      )}
      <Snackbar
        open={!!inquiry.snackbar}
        autoHideDuration={4000}
        onClose={(_, reason) => { if (reason !== 'clickaway' || !inquiry.snackbar?.undo) setSnackbar(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {inquiry.snackbar ? (
          <Alert
            severity={inquiry.snackbar.severity}
            onClose={() => setSnackbar(null)}
            action={viewId !== undefined
              ? <Button color="inherit" size="small" onClick={() => { setSnackbar(null); navigate({ pathname: `/inquiries/${viewId}`, search }); }}>View</Button>
              : inquiry.snackbar.undo
                ? <Button color="inherit" size="small" onClick={inquiry.snackbar.undo}>Undo</Button>
                : undefined}
          >
            {inquiry.snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Paper>
  );
}
